// src/services/api/auth.ts
import { httpClient } from '@/services/http/client';
import { ApiResponse } from '@/services/types/api';
import { UserData, AuthTokens } from '@/services/auth/storage';

// Datos para login
export interface LoginCredentials {
    email: string;
    password: string;
}

// Datos para registro
export interface RegisterData {
    username: string;
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    phone?: string;
}

// Datos para resetear contraseña
export interface ResetPasswordData {
    token: string;
    password: string;
    password_confirmation: string;
}

// Respuesta de autenticación
export interface AuthResponse {
    user: UserData;
    tokens: AuthTokens;
}

// Actualización de perfil
export interface UpdateProfileData {
    first_name?: string;
    last_name?: string;
    phone?: string;
    profile_image?: string;
}

class AuthApi {
    private readonly basePath = '/auth';

    /**
     * Iniciar sesión
     */
    async login(credentials: LoginCredentials): Promise<AuthResponse> {
        const response = await httpClient.post<ApiResponse<AuthResponse>>(
            `${this.basePath}/login`,
            credentials
        );
        return response.data.data;
    }

    /**
     * Registrar nuevo usuario
     */
    async register(userData: RegisterData): Promise<AuthResponse> {
        const response = await httpClient.post<ApiResponse<AuthResponse>>(
            `${this.basePath}/register`,
            userData
        );
        return response.data.data;
    }

    /**
     * Cerrar sesión
     */
    async logout(): Promise<void> {
        await httpClient.post(`${this.basePath}/logout`);
    }

    /**
     * Refrescar token de acceso
     */
    async refreshToken(refreshToken: string): Promise<AuthTokens> {
        const response = await httpClient.post<ApiResponse<AuthTokens>>(
            `${this.basePath}/refresh`,
            { refresh_token: refreshToken }
        );
        return response.data.data;
    }

    /**
     * Obtener información del usuario actual
     */
    async getCurrentUser(): Promise<UserData> {
        const response = await httpClient.get<ApiResponse<UserData>>(
            `${this.basePath}/me`
        );
        return response.data.data;
    }

    /**
     * Actualizar perfil del usuario
     */
    async updateProfile(data: UpdateProfileData): Promise<UserData> {
        console.log("vea", data);
        const response = await httpClient.put<ApiResponse<UserData>>(
            '/user/profile',
            data
        );
        return response.data.data;
    }

    /**
     * Solicitar reseteo de contraseña
     */
    async forgotPassword(email: string): Promise<void> {
        await httpClient.post(`${this.basePath}/forgot-password`, { email });
    }

    /**
     * Resetear contraseña
     */
    async resetPassword(data: ResetPasswordData): Promise<void> {
        await httpClient.post(`${this.basePath}/reset-password`, data);
    }

    /**
     * Verificar email
     */
    /*async verifyEmail(token: string): Promise<void> {
        await httpClient.post(`${this.basePath}/verify-email`, { token });
    }*/

    /**
     * Reenviar email de verificación
     */
    async resendVerificationEmail(): Promise<void> {
        await httpClient.post(`${this.basePath}/resend-verification`);
    }

    /**
     * Cambiar contraseña (usuario autenticado)
     */
    async changePassword(data: {
        current_password: string;
        new_password: string;
        new_password_confirmation: string;
    }): Promise<void> {
        await httpClient.put('/user/change-password', data);
    }
    async sendVerificationEmail(): Promise<{ message: string }> {
        const res = await httpClient.post<ApiResponse<{ message: string }>>('/auth/send-verification-email');
        return res.data.data;
    }
    async verifyEmail(token: string): Promise<UserData> {
        const res = await httpClient.post<ApiResponse<UserData>>('/auth/verify-email', { token });
        return res.data.data;
    }
    /**
     * Eliminar cuenta
     */
    async deleteAccount(password: string): Promise<void> {
        await httpClient.delete('/user/account', {
            data: { password }
        });
    }

    /**
     * Verificar si el email está disponible
     */
    async checkEmailAvailability(email: string): Promise<boolean> {
        try {
            const response = await httpClient.get<ApiResponse<{ available: boolean }>>(
                `/auth/check-email?email=${encodeURIComponent(email)}`
            );
            return response.data.data.available;
        } catch (error) {
            return false;
        }
    }

    /**
     * Verificar si el username está disponible
     */
    async checkUsernameAvailability(username: string): Promise<boolean> {
        try {
            const response = await httpClient.get<ApiResponse<{ available: boolean }>>(
                `/auth/check-username?username=${encodeURIComponent(username)}`
            );
            return response.data.data.available;
        } catch (error) {
            return false;
        }
    }
    async getUserProfile(): Promise<UserData> {
        const res = await httpClient.get<ApiResponse<UserData>>('user/profile');
        return res.data.data;
    }

    // Debe aceptar multipart y devolver { profile_image: string } o similar
    async updateAvatar(file: File): Promise<{ profile_image: string }> {
        const form = new FormData();
        form.append('avatar', file);
        const res = await httpClient.post<ApiResponse<{ profile_image: string }>>('user/profile/avatar', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return res.data.data;
    }
    /*async requestPasswordReset(payload: { email: string }): Promise<{ message: string }> {
        const res = await httpClient.post<ApiResponse<{ message: string }>>('/auth/forgot-password', payload);
        return res.data.data;
    }

    async resetPassword(payload: { token: string; email: string; password: string; password_confirmation: string }): Promise<{ message: string }> {
        const res = await httpClient.post<ApiResponse<{ message: string }>>('/auth/reset-password', payload);
        return res.data.data;
    }*/
}

// Instancia singleton
export const authApi = new AuthApi();